import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { chatApi, streamMessage, streamRegenerate } from "./chatbotApi";
import {
    subscribe as subscribeStream,
    getStreamState,
    isStreamingFor,
    abortStream,
    resetStream,
    runStream as runStoreStream,
} from "./streamStore";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import ProviderConfigModal from "./ProviderConfigModal";
import MembersModal from "./MembersModal";
import { TypingMark, TypingLabel, PausaScrittura } from "./TypingIndicator";
import "./Chatbot.css";

// Componenti custom per il markdown delle risposte dell'assistente:
// - link: aprono in una nuova scheda (sicurezza: noopener noreferrer)
// - table: avvolta in un contenitore che scrolla in orizzontale su schermi stretti
//   (regola CSS del progetto: il contenuto largo non deve allargare la pagina)
const mdComponents = {
    a: ({ children, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    ),
    table: ({ children }) => (
        <div className="chatbot-table-wrap">
            <table>{children}</table>
        </div>
    ),
};

// I modelli OpenAI emettono la matematica coi delimitatori LaTeX \( ... \) e
// \[ ... \], che remark-math NON riconosce (usa $ ... $ e $$ ... $$). Senza
// questa conversione le formule comparivano come testo grezzo ("caratteri
// strani"). Vale anche retroattivamente sui messaggi già salvati.
function preprocessMath(text) {
    let r = text.replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `$$${inner}$$`);
    r = r.replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner) => `$${inner}$`);
    return r;
}

// Il markdown di una risposta, MEMOIZZATO sul testo. Il parse (remark + KaTeX) è la
// parte più costosa del render, e durante lo streaming la lista dei messaggi viene
// ridisegnata a ogni token: senza memo si riparsavano TUTTE le risposte della chat
// decine di volte al secondo, e su una chat con qualche risposta lunga la pagina si
// bloccava finché la generazione non finiva. Così si riparsa solo la bolla che cambia.
const MarkdownContent = React.memo(function MarkdownContent({ content }) {
    return (
        <div className="chatbot-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={mdComponents}>
                {preprocessMath(content)}
            </ReactMarkdown>
        </div>
    );
});

// Dimensione file leggibile (B / KB / MB).
const formatBytes = (n) => {
    if (!n || n < 1024) return `${n || 0} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Accorcia il nome file NEL MEZZO tenendo inizio ed estensione: di un
// "label_5a7de70f-7340-494a-8ed9-5b90d13dd2f1.pdf" i caratteri centrali non dicono
// nulla, mentre l'estensione tagliata via fa perdere di che file si tratta.
const nomeAbbreviato = (nome = "", max = 28) => {
    if (nome.length <= max) return nome;
    const punto = nome.lastIndexOf(".");
    const est = punto > 0 ? nome.slice(punto) : "";
    const base = punto > 0 ? nome.slice(0, punto) : nome;
    const spazio = Math.max(8, max - est.length - 1);
    const testa = Math.ceil(spazio * 0.65);
    const coda = spazio - testa;
    return `${base.slice(0, testa)}…${coda > 0 ? base.slice(-coda) : ""}${est}`;
};

// Etichetta breve del tipo file (dall'estensione), per il "chip" dell'allegato.
const fileExtLabel = (filename = "") => {
    const ext = (filename.split(".").pop() || "").toLowerCase();
    const map = { xlsx: "XLS", xls: "XLS", csv: "CSV", docx: "DOC", doc: "DOC", pptx: "PPT", ppt: "PPT", pdf: "PDF", txt: "TXT" };
    return map[ext] || ext.toUpperCase() || "FILE";
};

// Allegati dentro una bolla: immagini come miniature cliccabili, gli altri come
// chip scaricabili. I file GENERATI dall'assistente mostrano l'azione "Scarica".
const MessageAttachments = ({ attachments }) => {
    if (!attachments || attachments.length === 0) return null;
    return (
        <div className="chatbot-attachments">
            {attachments.map((a) =>
                a.kind === "image" ? (
                    <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="chatbot-att-image" title={a.filename}>
                        <img src={a.url} alt={a.filename} />
                    </a>
                ) : (
                    <a
                        key={a.id}
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`chatbot-att-file ${a.kind === "generated" ? "generated" : ""}`}
                        title={a.filename}
                    >
                        <span className="chatbot-att-ext">{fileExtLabel(a.filename)}</span>
                        <span className="chatbot-att-info">
                            <span className="chatbot-att-name">{a.filename}</span>
                            <span className="chatbot-att-sub">
                                {a.kind === "generated" ? "Scarica ↓" : formatBytes(a.size)}
                            </span>
                        </span>
                    </a>
                )
            )}
        </div>
    );
};

// Altezza massima delle textarea prima che torni lo scroll interno (~8 righe).
const TEXTAREA_MAX_HEIGHT = 200;

// Adatta l'altezza di una textarea al contenuto. L'azzeramento prima della misura è
// necessario: senza, scrollHeight non decresce mai quando si cancella del testo.
const autoResize = (el, maxHeight) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
};

// Chatbot AI — struttura ChatGPT-like. Condivisione "a membri": il proprietario
// della chat aggiunge altri utenti con accesso al modulo (permesso ai_agents.chatbot),
// che possono vedere E usare la chat (collaborativa). Risposte in streaming SSE.
const Chatbot = () => {
    const navigate = useNavigate();
    const role = localStorage.getItem("role");
    const username = localStorage.getItem("username") || "";
    const isSuperadmin = role === "superadmin";

    // models: [{ provider_type, label, models: [], default_model }].
    // Task 452: `models` contiene già solo i modelli NON dismessi dal provider (il filtro
    // lo fa il server), e `default_model` è quello con cui si preseleziona la tendina.
    const [models, setModels] = useState([]);
    const [provider, setProvider] = useState("");
    const [model, setModel] = useState("");

    const [conversations, setConversations] = useState([]);
    // Task 452: la conversazione aperta sopravvive al cambio pagina nella dashboard.
    // sessionStorage e non localStorage: è contesto di navigazione, non una preferenza,
    // e deve morire con la scheda.
    const [activeId, setActiveId] = useState(() => {
        const saved = sessionStorage.getItem("chatbotActiveId");
        return saved ? Number(saved) : null; // null = nuova chat non ancora salvata
    });
    const [activeIsOwner, setActiveIsOwner] = useState(true);
    const [activeMembers, setActiveMembers] = useState([]);
    const [messages, setMessages] = useState([]);

    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const [showConfig, setShowConfig] = useState(false); // modal config provider (admin)
    const [showMembers, setShowMembers] = useState(false); // modal membri (solo owner)
    const [convOpen, setConvOpen] = useState(false); // sidebar conversazioni su mobile (overlay)
    const [editingId, setEditingId] = useState(null); // conversazione in fase di rinomina
    const [editTitle, setEditTitle] = useState("");
    const [searchOpen, setSearchOpen] = useState(false); // barra "Cerca"
    const [search, setSearch] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null); // conversazione da eliminare (modal)
    const [copiedId, setCopiedId] = useState(null); // messaggio che ha appena mostrato "Copiato"
    const [editingMsgId, setEditingMsgId] = useState(null); // messaggio utente in modifica inline
    const [editMsgText, setEditMsgText] = useState("");

    const [activeIsFavorite, setActiveIsFavorite] = useState(false); // preferito della chat aperta
    const [attachments, setAttachments] = useState([]); // allegati caricati, in attesa del prossimo invio
    const [uploading, setUploading] = useState(false); // upload allegati in corso
    const [uploadInfo, setUploadInfo] = useState(""); // cosa si sta caricando, mostrato durante l'attesa
    const [toolStatus, setToolStatus] = useState(""); // es. "Sto generando un file Excel…"

    const endRef = useRef(null);
    const fileInputRef = useRef(null); // input file nascosto per gli allegati
    const skipScrollRef = useRef(false); // salta l'auto-scroll al prossimo cambio messaggi (es. cambio versione)
    // Segui la risposta solo se l'utente è in fondo (task 504): se scorre verso l'alto
    // per leggere mentre il modello scrive, la pagina resta dov'è. Torna a seguire
    // quando lui torna in fondo, quando invia un messaggio o apre una chat.
    const seguiInFondoRef = useRef(true);
    const ultimoScrollTopRef = useRef(0);
    const [staiLeggendoSopra, setStaiLeggendoSopra] = useState(false); // mostra il pulsante "↓"
    const activeIdRef = useRef(activeId); // id corrente leggibile dalle callback dello store
    const modelRef = useRef(model); // modello richiesto, per accorgersi del fallback a fine stream
    const inputRef = useRef(null); // textarea di input, per l'auto-resize
    const editMsgRef = useRef(null); // textarea di modifica messaggio, idem

    const reloadConversations = useCallback(() => {
        chatApi.listConversations().then(setConversations).catch(() => {});
    }, []);

    const loadModels = useCallback(() => {
        return chatApi.listModels().then((data) => {
            setModels(data);
            setProvider((prev) => {
                const target = data.find((m) => m.provider_type === prev) || data[0];
                if (!target) {
                    setModel("");
                    return "";
                }
                // Preselezione: il modello consigliato dal server. Se quello già scelto è
                // ancora in elenco lo si tiene; se nel frattempo è stato dismesso il
                // server non lo restituisce più e si ricade sul consigliato.
                setModel((prevModel) =>
                    prevModel && target.models.includes(prevModel) ? prevModel : target.default_model
                );
                return target.provider_type;
            });
        });
    }, []);

    useEffect(() => {
        loadModels().catch(() => setError("Impossibile caricare i modelli disponibili."));
        reloadConversations();
    }, [loadModels, reloadConversations]);

    // --- Stream persistente al cambio pagina (task 452) ---

    // L'id aperto serve alle callback dello store, che vivono fuori dal ciclo di render
    // e vedrebbero altrimenti un valore congelato al momento della sottoscrizione.
    useEffect(() => {
        modelRef.current = model;
    }, [model]);

    useEffect(() => {
        activeIdRef.current = activeId;
        if (activeId) sessionStorage.setItem("chatbotActiveId", String(activeId));
        else sessionStorage.removeItem("chatbotActiveId");
    }, [activeId]);

    // Riversa lo stato dello store nello state locale: il resto del componente continua
    // a leggere `messages`/`sending` come prima. Al mount recupera anche una generazione
    // partita prima che si uscisse dalla pagina.
    useEffect(() => {
        const sync = (s) => {
            if (!s.convId) return;
            const open = activeIdRef.current;
            // Se nel frattempo l'utente ha aperto un'altra chat, non gliela sovrascrivo.
            if (open !== null && open !== s.convId) return;
            setActiveId((prev) => prev ?? s.convId);
            setMessages(s.messages);
            setSending(s.sending);
            setToolStatus(s.toolStatus);
            if (s.error) setError(s.error);
        };
        sync(getStreamState());
        return subscribeStream(sync);
    }, []);

    // Rientro nella pagina: riapro la conversazione su cui ero rimasto.
    useEffect(() => {
        const saved = sessionStorage.getItem("chatbotActiveId");
        if (saved) openConversation(Number(saved));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Il cambio versione (freccette) aggiorna i messaggi ma NON deve scrollare in fondo.
        if (skipScrollRef.current) {
            skipScrollRef.current = false;
            return;
        }
        // Durante lo streaming lo scroll è ISTANTANEO: con "smooth" ogni aggiornamento
        // della bolla (decine al secondo) avviava una nuova animazione sopra quella
        // precedente, mai conclusa, e la pagina scattava — peggio ancora rientrando in
        // una chat con la risposta in corso. L'animazione resta per i cambi "umani":
        // apertura di una chat, invio di un messaggio, cambio versione.
        if (!seguiInFondoRef.current) return; // l'utente sta leggendo più su: non lo si sposta
        endRef.current?.scrollIntoView({ behavior: sending ? "auto" : "smooth" });
    }, [messages, sending]);

    // Torna a seguire la risposta e va in fondo: invio, apertura chat, pulsante "↓".
    const seguiDiNuovo = () => {
        seguiInFondoRef.current = true;
        setStaiLeggendoSopra(false);
    };

    // Chi ha spostato la pagina? Lo scroll automatico va SOLO verso il basso (e la crescita
    // del testo non sposta scrollTop): uno spostamento verso l'alto è per forza dell'utente.
    // Tornato vicino al fondo (80px), si riprende a seguire.
    const onScrollMessaggi = (e) => {
        const el = e.currentTarget;
        const inFondo = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        if (inFondo) seguiInFondoRef.current = true;
        else if (el.scrollTop < ultimoScrollTopRef.current - 2) seguiInFondoRef.current = false;
        ultimoScrollTopRef.current = el.scrollTop;
        const sopra = !seguiInFondoRef.current;
        setStaiLeggendoSopra((prev) => (prev === sopra ? prev : sopra));
    };

    // La textarea di input cresce col contenuto fino a TEXTAREA_MAX_HEIGHT. Agganciata al
    // valore di `input` e non a onChange, così copre anche gli svuotamenti programmatici
    // (invio del messaggio, apertura di una nuova chat).
    useEffect(() => {
        autoResize(inputRef.current, TEXTAREA_MAX_HEIGHT);
    }, [input]);

    // Stessa cosa per la modifica inline di un messaggio già inviato: `editingMsgId` nelle
    // dipendenze perché la textarea nasce montata con il testo già dentro.
    useEffect(() => {
        autoResize(editMsgRef.current, TEXTAREA_MAX_HEIGHT);
    }, [editMsgText, editingMsgId]);

    const providerEntry = models.find((m) => m.provider_type === provider);
    const modelsForProvider = providerEntry?.models || [];
    // Modelli scoperti dal backend dopo l'ultima revisione del catalogo (task 485):
    // in tendina con l'etichetta "nuovo", mai preselezionati. Chi vuole li prova.
    const newModels = new Set(providerEntry?.new_models || []);
    const isNewChat = activeId === null;
    const isShared = activeMembers.length > 0; // mostra l'autore sui messaggi utente
    const filteredConversations = search.trim()
        ? conversations.filter((c) => (c.title || "").toLowerCase().includes(search.trim().toLowerCase()))
        : conversations;
    const favoriteConversations = filteredConversations.filter((c) => c.is_favorite);
    const recentConversations = filteredConversations.filter((c) => !c.is_favorite);

    const newChat = () => {
        setActiveId(null);
        setActiveIsOwner(true);
        setActiveMembers([]);
        setActiveIsFavorite(false);
        setMessages([]);
        setInput("");
        setAttachments([]);
        setError("");
        setConvOpen(false);
        if (models.length > 0) {
            setProvider(models[0].provider_type);
            setModel(models[0].default_model);
        }
    };

    const openConversation = async (id) => {
        seguiDiNuovo();
        setError("");
        setConvOpen(false);
        setAttachments([]);
        try {
            const data = await chatApi.getConversation(id);
            setActiveId(id);
            setActiveIsOwner(!!data.conversation.is_owner);
            setActiveMembers(data.members || []);
            setActiveIsFavorite(!!data.conversation.is_favorite);
            // Se su questa conversazione c'è una generazione in volo (rientro nella
            // pagina o da un'altra chat), i messaggi buoni sono quelli dello store: dal
            // DB manca ancora la risposta in corso e la sovrascriverei con una vista più
            // vecchia. E vanno presi SUBITO: aspettare il prossimo evento dello stream
            // lasciava a schermo la chat precedente anche per 20-30 secondi, perché le
            // fasi mute (una ricerca sul web, la lettura di una pagina) non emettono nulla.
            if (isStreamingFor(id)) {
                const s = getStreamState();
                setMessages(s.messages);
                setSending(s.sending);
                setToolStatus(s.toolStatus);
            } else {
                setMessages(data.messages);
            }
            setProvider(data.conversation.provider_type);
            setModel(data.conversation.model);
        } catch {
            setError("Impossibile aprire la conversazione.");
        }
    };

    // Apre il modal di conferma (invece dell'alert nativo del browser).
    const askDelete = (c, e) => {
        e.stopPropagation();
        setDeleteTarget(c);
    };

    const confirmDelete = async () => {
        const id = deleteTarget?.id;
        setDeleteTarget(null);
        if (!id) return;
        try {
            await chatApi.deleteConversation(id);
            if (activeId === id) newChat();
            reloadConversations();
        } catch {
            setError("Errore durante l'eliminazione della conversazione.");
        }
    };

    // --- Rinomina inline (solo proprietario) ---
    const startRename = (c, e) => {
        e.stopPropagation();
        setEditTitle(c.title || "");
        setEditingId(c.id);
    };

    const commitRename = async () => {
        const id = editingId;
        const title = editTitle.trim();
        setEditingId(null);
        if (!id || !title) return;
        setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
        try {
            await chatApi.renameConversation(id, title);
        } catch {
            reloadConversations();
        }
    };

    const onRenameKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commitRename();
        } else if (e.key === "Escape") {
            e.preventDefault();
            setEditingId(null);
        }
    };

    // Cambiando provider si riparte dal modello consigliato per quel provider.
    const changeProvider = (pt) => {
        setProvider(pt);
        setModel(models.find((m) => m.provider_type === pt)?.default_model || "");
    };

    // Cambio modello. Su chat nuova aggiorna solo lo stato (si fissa alla creazione);
    // su chat avviata lo persiste subito.
    const changeModel = async (next) => {
        const prev = model;
        setModel(next);
        if (isNewChat) return;
        try {
            await chatApi.updateModel(activeId, next);
            setError("");
            reloadConversations();
        } catch (err) {
            setModel(prev); // rollback: il cambio non è stato salvato
            setError(err.response?.data?.error || "Impossibile cambiare il modello.");
        }
    };

    // --- Allegati ---
    // Gli allegati vanno caricati SU una conversazione: se sono in una chat nuova
    // non ancora salvata, la creo ora (con provider/model correnti) e ne fisso l'id.
    const ensureConversation = async () => {
        if (activeId) return activeId;
        const conv = await chatApi.createConversation(provider, model);
        setActiveId(conv.id);
        reloadConversations();
        return conv.id;
    };

    const openFilePicker = () => fileInputRef.current?.click();

    const onFilesPicked = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ""; // consente di ricaricare lo stesso file
        if (!files.length) return;
        setError("");
        setUploading(true);
        // Un PDF scansionato passa dall'OCR pagina per pagina e può metterci minuti:
        // senza scriverlo, la graffetta che gira da sola sembra un blocco e l'utente
        // ricarica la pagina a metà lettura.
        const nomi = files.length > 1 ? `${files.length} file` : files[0].name;
        const pdf = files.some((f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name));
        setUploadInfo(
            pdf
                ? `Caricamento di ${nomi}… se è una scansione la lettura del testo può richiedere qualche minuto.`
                : `Caricamento di ${nomi}…`
        );
        try {
            const convId = await ensureConversation();
            const res = await chatApi.uploadAttachments(convId, files, (p) => {
                const nome = nomeAbbreviato(p.file, 22);
                if (p.fase === "ocr" && p.totale) {
                    setUploadInfo(`${nome} — lettura pagina ${p.pagina} di ${p.totale}`);
                } else if (p.fase === "ocr") {
                    setUploadInfo(`${nome} — lettura del testo`);
                } else if (p.fase === "analisi") {
                    setUploadInfo(`${nome} — analisi del documento`);
                } else {
                    setUploadInfo(`${nome} — caricamento`);
                }
            });
            const ok = (res.attachments || []).filter((a) => !a.error);
            const bad = (res.attachments || []).filter((a) => a.error);
            if (ok.length) setAttachments((prev) => [...prev, ...ok]);
            // Col motivo, se il server lo dà: "foto.jpg (formato immagine non supportato…)".
            if (bad.length) {
                setError(`File non caricati: ${bad.map((b) => (b.reason ? `${b.filename} (${b.reason})` : b.filename)).join(", ")}`);
            }
        } catch (err) {
            // Non più axios: l'errore arriva da readSSE come Error, o come JSON se il
            // server ha rifiutato prima di aprire lo stream.
            setError(err.message || "Errore durante il caricamento degli allegati.");
        } finally {
            setUploading(false);
            setUploadInfo("");
        }
    };

    const removeAttachment = (attId) => setAttachments((prev) => prev.filter((a) => a.id !== attId));

    // --- Preferiti ---
    const toggleFavorite = async (conv, e) => {
        e?.stopPropagation();
        const id = conv.id;
        const next = !conv.is_favorite;
        setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, is_favorite: next } : c)));
        if (id === activeId) setActiveIsFavorite(next);
        try {
            if (next) await chatApi.addFavorite(id);
            else await chatApi.removeFavorite(id);
        } catch {
            reloadConversations();
        }
    };

    // Delega il consumo dello stream allo store di modulo (streamStore.js), che vive
    // fuori dal componente e quindi continua a girare anche a pagina smontata.
    // A fine generazione ricarica il percorso attivo: id reali + info versioni + allegati.
    const consumeStream = (iterable, ctrl, placeholderId, convId, optimisticMessages) =>
        runStoreStream({
            iterable,
            ctrl,
            convId,
            placeholderId,
            messages: optimisticMessages,
            onDone: async ({ error: streamError, interrupted }) => {
                const stillOpen = () => activeIdRef.current === convId || activeIdRef.current === null;
                try {
                    if (streamError) {
                        if (stillOpen()) setMessages((prev) => prev.filter((m) => m.id !== placeholderId));
                    } else if (!interrupted) {
                        // `interrupted` = troncato dal client perché lo Stop lato server
                        // non è andato a buon fine: si tiene quello che è a schermo. Nel
                        // caso normale il server ha già salvato il parziale, quindi si
                        // ricarica come dopo una risposta completa.
                        const full = await chatApi.getConversation(convId);
                        if (stillOpen()) {
                            setMessages(full.messages);
                            // Se il modello era stato dismesso il server ha scalato al
                            // successivo: la tendina deve mostrare quello davvero in uso.
                            setModel(full.conversation.model);
                            // ...e va ricaricato anche l'ELENCO, altrimenti il modello
                            // morto resta fra le opzioni fino al prossimo F5.
                            if (full.conversation.model !== modelRef.current) {
                                loadModels().catch(() => {});
                            }
                        }
                    }
                    reloadConversations();
                } finally {
                    // Lo store torna vuoto: senza, al prossimo mount si riaggancerebbe a
                    // una generazione già conclusa mostrando messaggi ottimistici stantii.
                    resetStream();
                }
            },
        });

    // Runner per invio e modifica. baseMessages = messaggi su cui costruire (invio: attuali;
    // modifica: fino a prima del messaggio modificato). editMessageId valorizzato = modifica.
    // sentAttachments = allegati da collegare a questo messaggio utente.
    const runStream = async ({ content, editMessageId, baseMessages, sentAttachments = [] }) => {
        seguiDiNuovo();
        setError("");
        setSending(true);
        const placeholderId = `a-${Date.now()}`;
        // Ottimistico: base + messaggio utente (con autore + allegati) + bolla assistente vuota
        const optimistic = [
            ...baseMessages,
            { id: `u-${Date.now()}`, role: "user", content, author_username: username, attachments: sentAttachments },
            { id: placeholderId, role: "assistant", content: "" },
        ];
        setMessages(optimistic);
        const ctrl = new AbortController();
        let convId = activeId;
        if (!convId) {
            try {
                const conv = await chatApi.createConversation(provider, model);
                convId = conv.id;
                setActiveId(convId);
                activeIdRef.current = convId; // subito, senza aspettare il prossimo render
            } catch (err) {
                setError(err.message || "Errore creazione conversazione.");
                setMessages((prev) => prev.filter((m) => m.id !== placeholderId));
                setSending(false);
                return;
            }
        }
        const attachmentIds = sentAttachments.map((a) => a.id);
        await consumeStream(
            streamMessage(convId, content, ctrl.signal, editMessageId, attachmentIds),
            ctrl,
            placeholderId,
            convId,
            optimistic
        );
    };

    // Rigenera la risposta: crea una nuova versione (fratello) dell'assistente e la streamma.
    const runRegenerate = (m) => {
        if (sending || !activeId) return;
        setError("");
        setSending(true);
        // Base = tutto fino a PRIMA della risposta corrente (che viene rigenerata).
        const idx = messages.findIndex((x) => x.id === m.id);
        const base = idx >= 0 ? messages.slice(0, idx) : messages;
        const placeholderId = `a-${Date.now()}`;
        const optimistic = [...base, { id: placeholderId, role: "assistant", content: "" }];
        setMessages(optimistic);
        const ctrl = new AbortController();
        consumeStream(streamRegenerate(activeId, m.id, ctrl.signal), ctrl, placeholderId, activeId, optimistic);
    };

    const send = () => {
        const text = input.trim();
        // Si può inviare con solo testo, solo allegati, o entrambi. Non durante upload/streaming.
        if ((!text && attachments.length === 0) || sending || uploading) return;
        const sentAttachments = attachments;
        setInput("");
        setAttachments([]);
        runStream({ content: text, baseMessages: messages, sentAttachments });
    };

    // --- Modifica messaggio (versioni con freccette, stile ChatGPT) ---
    const startEditMessage = (m) => {
        setEditingMsgId(m.id);
        setEditMsgText(m.content);
    };
    const cancelEditMessage = () => {
        setEditingMsgId(null);
        setEditMsgText("");
    };
    const saveEditMessage = (m) => {
        const text = editMsgText.trim();
        if (!text || sending) return;
        // Base = tutto ciò che precede il messaggio modificato (il resto è il ramo vecchio).
        const idx = messages.findIndex((x) => x.id === m.id);
        const base = idx >= 0 ? messages.slice(0, idx) : messages;
        setEditingMsgId(null);
        setEditMsgText("");
        runStream({ content: text, editMessageId: m.id, baseMessages: base });
    };
    // Cambia versione del turno: attiva il fratello prev/next e ricarica il ramo.
    const switchVersion = async (m, dir) => {
        const targetId = m.versions?.[m.version_index - 1 + dir];
        if (targetId == null) return;
        try {
            const res = await chatApi.activateMessage(activeId, targetId);
            skipScrollRef.current = true; // niente auto-scroll: resto dove sono
            setMessages(res.messages);
        } catch (e) {
            setError(e.response?.data?.error || "Errore cambio versione.");
        }
    };
    // Freccette ‹ n/m › per navigare le versioni (usate sia sui messaggi utente sia sulle risposte).
    const renderVersionArrows = (m) =>
        m.version_count > 1 ? (
            <div className="chatbot-versions">
                <button
                    type="button"
                    disabled={m.version_index <= 1 || sending}
                    onClick={() => switchVersion(m, -1)}
                    title="Versione precedente"
                >
                    ‹
                </button>
                <span>
                    {m.version_index}/{m.version_count}
                </span>
                <button
                    type="button"
                    disabled={m.version_index >= m.version_count || sending}
                    onClick={() => switchVersion(m, 1)}
                    title="Versione successiva"
                >
                    ›
                </button>
            </div>
        ) : null;

    // Ferma lo stream in corso: abortisce la fetch; il testo già arrivato resta.
    // L'AbortController sta nello store, non più nel componente.
    // Stop esplicito (task 452). Chiudere la connessione da qui non basterebbe: per il
    // backend sarebbe identico a un cambio pagina, che invece deve lasciar finire la
    // generazione. Dichiarandolo, il server interrompe la chiamata al provider e salva
    // solo il parziale già a schermo — come fa ChatGPT.
    const stop = async () => {
        const { convId } = getStreamState();
        if (!convId) return abortStream();
        try {
            await chatApi.stopGeneration(convId);
        } catch {
            // Server irraggiungibile: almeno tronco lo stream lato client.
            abortStream();
        }
    };

    // Copia il testo di un messaggio negli appunti e mostra "Copiato" per ~1.5s.
    const copyMessage = async (m) => {
        try {
            await navigator.clipboard.writeText(m.content || "");
            setCopiedId(m.id);
            setTimeout(() => setCopiedId((c) => (c === m.id ? null : c)), 1500);
        } catch {
            /* clipboard non disponibile (es. contesto non sicuro): ignoriamo */
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    // aggiorna la lista membri della chat attiva (dopo modifiche nel modal)
    const refreshMembers = () => {
        if (!activeId) return;
        chatApi.listMembers(activeId).then(setActiveMembers).catch(() => {});
    };

    // Chat vuota: l'input va al centro (stile ChatGPT). Definisco l'input una
    // volta sola e lo riuso sia al centro che in basso.
    const isEmpty = messages.length === 0 && !sending;
    // Da task 452 il modello deprecato non blocca più l'invio: se ne occupa il server,
    // che scala da solo al primo modello ancora disponibile del provider.
    const canSend =
        !sending && !uploading && (input.trim().length > 0 || attachments.length > 0);
    const inputBar = (
        <div className="chatbot-input-wrap">
            {uploading && (
                <div className="chatbot-upload-status">
                    <span className="chatbot-attach-spinner" aria-hidden="true" />
                    <span className="chatbot-upload-status-text">{uploadInfo}</span>
                </div>
            )}
            {attachments.length > 0 && (
                <div className="chatbot-input-attachments">
                    {attachments.map((a) => (
                        <div key={a.id} className="chatbot-att-chip" title={a.filename}>
                            {a.kind === "image" ? (
                                <img src={a.url} alt={a.filename} className="chatbot-att-chip-thumb" />
                            ) : (
                                <span className="chatbot-att-chip-ext">{fileExtLabel(a.filename)}</span>
                            )}
                            <span className="chatbot-att-chip-info">
                                <span className="chatbot-att-chip-name">{nomeAbbreviato(a.filename)}</span>
                                <span className="chatbot-att-chip-sub">{formatBytes(a.size)}</span>
                            </span>
                            <button
                                type="button"
                                className="chatbot-att-chip-x"
                                onClick={() => removeAttachment(a.id)}
                                title="Rimuovi allegato"
                                aria-label="Rimuovi allegato"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="chatbot-input-bar">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={onFilesPicked}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.tsv,.txt,.md,.json,.xml"
                />
                <button
                    type="button"
                    className="chatbot-attach-btn"
                    onClick={openFilePicker}
                    disabled={models.length === 0 || uploading}
                    title={uploading ? "Caricamento in corso…" : "Allega immagini o documenti"}
                    aria-label="Allega file"
                >
                    {uploading ? (
                        <span className="chatbot-attach-spinner" aria-hidden="true" />
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                    )}
                </button>
                <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Scrivi un messaggio..."
                    rows={1}
                    disabled={models.length === 0}
                />
                <button
                    onClick={sending ? stop : send}
                    disabled={models.length === 0 || (!sending && !canSend)}
                    className={sending ? "chatbot-stop-btn" : ""}
                    title={sending ? "Interrompi" : "Invia"}
                    aria-label={sending ? "Interrompi" : "Invia"}
                >
                    {sending ? (
                        <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true">
                            <rect x="6" y="6" width="12" height="12" rx="2.5" fill="currentColor" />
                        </svg>
                    ) : (
                        "Invia"
                    )}
                </button>
            </div>
        </div>
    );

    // Voce singola della sidebar (usata sia per Preferiti sia per Recenti).
    const renderConvItem = (c) => (
        <div
            key={c.id}
            className={`chatbot-conv-item ${c.id === activeId ? "active" : ""}`}
            onClick={() => (editingId === c.id ? null : openConversation(c.id))}
            role="button"
            tabIndex={0}
        >
            {editingId === c.id ? (
                <input
                    className="chatbot-conv-edit"
                    value={editTitle}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={onRenameKeyDown}
                    onBlur={commitRename}
                />
            ) : (
                <>
                    <button
                        type="button"
                        className={`chatbot-conv-fav ${c.is_favorite ? "on" : ""}`}
                        title={c.is_favorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                        onClick={(e) => toggleFavorite(c, e)}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={c.is_favorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    </button>
                    <span className="chatbot-conv-title">{c.title || "Nuova chat"}</span>
                    <div className="chatbot-conv-actions">
                        {(!c.is_owner || c.member_count > 0) && (
                            <span
                                className="chatbot-conv-shared"
                                title={c.is_owner ? "Condivisa" : "Condivisa con te"}
                            >
                                condivisa
                            </span>
                        )}
                        {c.is_owner && (
                            <>
                                <button
                                    className="chatbot-conv-edit-btn"
                                    title="Rinomina"
                                    onClick={(e) => startRename(c, e)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                    </svg>
                                </button>
                                <button
                                    className="chatbot-conv-del"
                                    title="Elimina"
                                    onClick={(e) => askDelete(c, e)}
                                >
                                    ×
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className="chatbot-page">
            <header className="page-header">
                <div className="header-content">
                    <button
                        className="chatbot-back"
                        onClick={() => navigate("/ai-agents")}
                        title="Torna ad AI Agents"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        <span className="chatbot-back-label">Indietro</span>
                    </button>
                    <div className="header-title">
                        <h2>LF23 Chatbot</h2>
                        <p className="header-subtitle">
                            Assistente conversazionale multi-provider (ChatGPT, Claude, …)
                        </p>
                    </div>
                </div>
            </header>

            <div className="chatbot-body">
                {convOpen && <div className="chatbot-backdrop" onClick={() => setConvOpen(false)} />}

                {/* Sidebar: storico conversazioni (proprie + condivise con me) */}
                <aside className={`chatbot-sidebar ${convOpen ? "open" : ""}`}>
                    <div className="chatbot-sidebar-header">
                        <div className="chatbot-brand">
                            <svg className="chatbot-brand-mark" width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <defs>
                                    <linearGradient id="lfChatbotGrad" x1="2" y1="3" x2="22" y2="21" gradientUnits="userSpaceOnUse">
                                        <stop offset="0" stopColor="#2E6DB1" />
                                        <stop offset="0.55" stopColor="#9333EA" />
                                        <stop offset="1" stopColor="#C5D042" />
                                    </linearGradient>
                                </defs>
                                <path d="M5 3.5h14A2.5 2.5 0 0 1 21.5 6v8A2.5 2.5 0 0 1 19 16.5h-8.5L6 20.5v-4H5A2.5 2.5 0 0 1 2.5 14V6A2.5 2.5 0 0 1 5 3.5z" fill="url(#lfChatbotGrad)" />
                                <circle cx="8.5" cy="10" r="1.25" fill="#fff" />
                                <circle cx="12" cy="10" r="1.25" fill="#fff" />
                                <circle cx="15.5" cy="10" r="1.25" fill="#fff" />
                            </svg>
                            <span className="chatbot-brand-text">LF23 <b>Chatbot</b></span>
                        </div>
                        <button
                            className="chatbot-sidebar-close"
                            onClick={() => setConvOpen(false)}
                            title="Chiudi"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                    <div className="chatbot-sidebar-menu">
                        <button className="chatbot-menu-item" onClick={newChat} title="Nuova chat">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                            <span>Nuova chat</span>
                        </button>
                        <button
                            className={`chatbot-menu-item ${searchOpen ? "active" : ""}`}
                            onClick={() => setSearchOpen((v) => !v)}
                            title="Cerca"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <span>Cerca</span>
                        </button>
                        {searchOpen && (
                            <input
                                className="chatbot-search-input"
                                autoFocus
                                placeholder="Cerca nelle chat…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        )}
                    </div>

                    <div className="chatbot-conv-list">
                        {filteredConversations.length === 0 && (
                            <p className="chatbot-empty-hint">
                                {search ? "Nessun risultato" : "Nessuna conversazione"}
                            </p>
                        )}
                        {favoriteConversations.length > 0 && (
                            <>
                                <div className="chatbot-recent-label">★ Preferiti</div>
                                {favoriteConversations.map(renderConvItem)}
                            </>
                        )}
                        {recentConversations.length > 0 && (
                            <>
                                <div className="chatbot-recent-label">Recenti</div>
                                {recentConversations.map(renderConvItem)}
                            </>
                        )}
                    </div>
                </aside>

                {/* Area chat */}
                <section className="chatbot-main">
                    <div className="chatbot-toolbar">
                        <div className="chatbot-toolbar-left">
                            <button
                                className="chatbot-icon-btn chatbot-conv-toggle"
                                onClick={() => setConvOpen((v) => !v)}
                                title="Conversazioni"
                            >
                                ☰
                            </button>
                            <div className="chatbot-model-picker">
                                <select
                                    value={provider}
                                    onChange={(e) => changeProvider(e.target.value)}
                                    disabled={!isNewChat}
                                    title={isNewChat ? "Provider" : "Il provider è fissato per la conversazione in corso"}
                                >
                                    {models.map((m) => (
                                        <option key={m.provider_type} value={m.provider_type}>
                                            {m.label}
                                        </option>
                                    ))}
                                </select>
                                {/* Task 452: la tendina elenca solo i modelli ancora disponibili
                                    (il server esclude i dismessi) ed è preselezionata sul più
                                    recente. Se un modello viene deprecato mentre è in uso, il
                                    server scala da solo al successivo — vedi llmService. */}
                                <select
                                    value={model}
                                    onChange={(e) => changeModel(e.target.value)}
                                    disabled={!isNewChat && !activeIsOwner}
                                    title={
                                        isNewChat
                                            ? "Modello"
                                            : activeIsOwner
                                            ? "Cambia il modello di questa conversazione"
                                            : "Solo il proprietario può cambiare il modello"
                                    }
                                >
                                    {/* Una chat vecchia può essere ferma su un modello dismesso, che
                                        non è più in elenco: lo mostro solo per non lasciare il
                                        <select> senza valore. Sparisce al primo invio, quando il
                                        server scala al modello nuovo. */}
                                    {model && !modelsForProvider.includes(model) && (
                                        <option value={model}>{model} (non più disponibile)</option>
                                    )}
                                    {modelsForProvider.map((mo) => (
                                        <option key={mo} value={mo}>
                                            {mo}
                                            {newModels.has(mo) ? " · nuovo" : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="chatbot-toolbar-right">
                            {!isNewChat && (
                                <button
                                    className={`chatbot-icon-btn ${activeIsFavorite ? "fav-on" : ""}`}
                                    onClick={() => toggleFavorite({ id: activeId, is_favorite: activeIsFavorite })}
                                    title={activeIsFavorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                                    aria-label="Preferito"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill={activeIsFavorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                    </svg>
                                </button>
                            )}
                            {!isNewChat && activeIsOwner && (
                                <button
                                    className="chatbot-icon-btn"
                                    onClick={() => setShowMembers(true)}
                                    title="Gestisci membri (condivisione)"
                                >
                                    Membri
                                </button>
                            )}
                            {/* Materiali grafici (task 453): stesso permesso del chatbot, quindi per tutti qui dentro */}
                            <button
                                className="chatbot-icon-btn"
                                onClick={() => navigate("/chatbot-ai/materiali")}
                                title="Materiali grafici dei brand: loghi, colori, font, piè di pagina"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                    <polyline points="21 15 16 10 5 21" />
                                </svg>
                                Materiali
                            </button>
                            {isSuperadmin && (
                                <button
                                    className="chatbot-icon-btn"
                                    onClick={() => setShowConfig(true)}
                                    title="Configura provider"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="3" />
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                    </svg>
                                    Provider
                                </button>
                            )}
                        </div>
                    </div>

                    {isEmpty ? (
                        <div className="chatbot-empty-center">
                            <h3 className="chatbot-empty-title">Come posso aiutarti?</h3>
                            <div className="chatbot-empty-input">
                                {error && <div className="chatbot-error">{error}</div>}
                                {inputBar}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="chatbot-messages" onScroll={onScrollMessaggi}>
                                {messages.map((m) => (
                                    <div key={m.id} className={`chatbot-msg chatbot-msg-${m.role}`}>
                                        {/* Logo animato a sinistra della bolla, solo mentre si aspetta
                                            la risposta: sparisce da sé al primo token, quando m.content
                                            si popola. */}
                                        {m.role === "assistant" && !m.content && !m.interrupted && sending && (
                                            <TypingMark />
                                        )}
                                        <div
                                            className={`chatbot-msg-inner${
                                                editingMsgId === m.id ? " chatbot-msg-inner-editing" : ""
                                            }`}
                                        >
                                            {editingMsgId === m.id ? (
                                                <div className="chatbot-edit-box">
                                                    <textarea
                                                        ref={editMsgRef}
                                                        className="chatbot-edit-textarea"
                                                        value={editMsgText}
                                                        onChange={(e) => setEditMsgText(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" && !e.shiftKey) {
                                                                e.preventDefault();
                                                                saveEditMessage(m);
                                                            } else if (e.key === "Escape") {
                                                                e.preventDefault();
                                                                cancelEditMessage();
                                                            }
                                                        }}
                                                        rows={2}
                                                        autoFocus
                                                    />
                                                    <div className="chatbot-edit-actions">
                                                        <button className="chatbot-edit-cancel" onClick={cancelEditMessage}>
                                                            Annulla
                                                        </button>
                                                        <button
                                                            className="chatbot-edit-save"
                                                            onClick={() => saveEditMessage(m)}
                                                            disabled={!editMsgText.trim()}
                                                        >
                                                            Salva e invia
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="chatbot-bubble">
                                                        {m.role === "user" && isShared && m.author_username && (
                                                            <span className="chatbot-bubble-author">{m.author_username}</span>
                                                        )}
                                                        {m.content ? (
                                                            m.role === "assistant" ? (
                                                                <>
                                                                    <MarkdownContent content={m.content} />
                                                                    {/* Risposta ferma a metà (strumento in preparazione):
                                                                        l'indicatore torna sotto il testo. Solo sull'ultimo
                                                                        messaggio, quello in arrivo. */}
                                                                    {sending && !m.interrupted && m.id === messages[messages.length - 1]?.id && (
                                                                        <PausaScrittura testo={m.content} label={toolStatus} />
                                                                    )}
                                                                </>
                                                            ) : (
                                                                m.content
                                                            )
                                                        ) : m.role === "assistant" && !m.interrupted && sending ? (
                                                            <TypingLabel label={toolStatus} />
                                                        ) : (
                                                            ""
                                                        )}
                                                        <MessageAttachments attachments={m.attachments} />
                                                        {m.interrupted && (
                                                            <div className="chatbot-interrupted">Interrupted</div>
                                                        )}
                                                    </div>
                                                    {m.role === "assistant" && m.content && (
                                                        <div className="chatbot-msg-actions">
                                                            {renderVersionArrows(m)}
                                                            <button
                                                                type="button"
                                                                className="chatbot-copy-btn"
                                                                onClick={() => copyMessage(m)}
                                                                title="Copia messaggio"
                                                            >
                                                                {copiedId === m.id ? (
                                                                    <>
                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                                            <polyline points="20 6 9 17 4 12" />
                                                                        </svg>
                                                                        Copiato
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                                        </svg>
                                                                        Copia
                                                                    </>
                                                                )}
                                                            </button>
                                                            {typeof m.id === "number" && (
                                                                <button
                                                                    type="button"
                                                                    className="chatbot-regen-btn"
                                                                    onClick={() => runRegenerate(m)}
                                                                    disabled={sending}
                                                                    title="Rigenera risposta"
                                                                >
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="23 4 23 10 17 10" />
                                                                        <polyline points="1 20 1 14 7 14" />
                                                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                                                    </svg>
                                                                    Rigenera
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    {m.role === "user" && typeof m.id === "number" && (
                                                        <div className="chatbot-msg-actions">
                                                            {renderVersionArrows(m)}
                                                            {!sending && (
                                                                <button
                                                                    type="button"
                                                                    className="chatbot-edit-btn"
                                                                    onClick={() => startEditMessage(m)}
                                                                    title="Modifica messaggio"
                                                                >
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M12 20h9" />
                                                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                                                    </svg>
                                                                    Modifica
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div ref={endRef} />
                                {sending && staiLeggendoSopra && (
                                    <button
                                        type="button"
                                        className="chatbot-vai-giu"
                                        title="Vai alla risposta in arrivo"
                                        aria-label="Vai alla risposta in arrivo"
                                        onClick={() => {
                                            seguiDiNuovo();
                                            endRef.current?.scrollIntoView({ behavior: "smooth" });
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path
                                                d="M12 5v13M6 12l6 6 6-6"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        {/* Puntino che pulsa: "qui sotto sta arrivando testo". */}
                                        <span className="chatbot-vai-giu-dot" aria-hidden="true" />
                                    </button>
                                )}
                            </div>

                            {error && <div className="chatbot-error">{error}</div>}

                            {inputBar}
                        </>
                    )}
                </section>
            </div>

            {/* Modal membri (condivisione) */}
            {showMembers && activeId && (
                <MembersModal
                    conversationId={activeId}
                    onClose={() => setShowMembers(false)}
                    onChanged={() => {
                        reloadConversations();
                        refreshMembers();
                    }}
                />
            )}

            {/* Modal config provider (admin/superadmin) */}
            {showConfig && <ProviderConfigModal onClose={() => setShowConfig(false)} onSaved={loadModels} />}

            {/* Modal conferma eliminazione conversazione */}
            {deleteTarget && (
                <div className="chatbot-modal-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="chatbot-modal chatbot-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Elimina conversazione</h3>
                        <p>
                            Vuoi eliminare “<strong>{deleteTarget.title || "Nuova chat"}</strong>”? L'operazione non è reversibile.
                        </p>
                        <div className="chatbot-modal-actions">
                            <button onClick={() => setDeleteTarget(null)}>Annulla</button>
                            <button className="chatbot-confirm-delete" onClick={confirmDelete}>
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chatbot;
